import { StandardEvents } from '@shopify/events';

/* ═══════════════════════════════════════════════════════════════════════════
   Variant bridge

   THE PROBLEM THIS SOLVES

   In the prototype, setColour(i) is a single function that touches eleven
   things: the colour name, the --lens custom property, the lens note, the light
   condition, the gallery, the sticky bar's thumbnail and colour name, and the
   two fit images in "Know it fits". All of it in one scope, on one page.

   In Shopify those eleven live in DIFFERENT SECTIONS. Horizon broadcasts its
   productSelect event on `this.closest('.shopify-section, dialog')` — correct
   for a product card in a grid, where two cards must not talk to each other,
   but it means product-fit, product-worn-by-you and the sticky buy bar never
   hear about a colour change at all.

   So: catch productSelect as it bubbles up to the document, resolve what the
   variant picker fetched, and re-broadcast it as `hystride:variant` with the
   variant data flattened out. Any section anywhere on the page can then listen
   to one event and update itself.

   docs/02-product-page.md §3 is explicit that whoever misses this ships a page
   where the price in the buy button lies.
   ═══════════════════════════════════════════════════════════════════════════ */

const EVENT = 'hystride:variant';

/**
 * Pull the variant JSON the main product section publishes for its current
 * selection. Rendered server-side by snippets/hystride-variant-data.liquid, so
 * it is always in step with the Liquid rather than a JS duplicate of the
 * metafield values.
 * @param {Document|DocumentFragment} [scope]
 */
function readVariantData(scope) {
  const el = (scope || document).querySelector('script[data-hys-variant-data]');
  if (!el?.textContent) return null;
  try {
    return JSON.parse(el.textContent);
  } catch {
    return null;
  }
}

/** @param {object} variant */
function broadcast(variant) {
  if (!variant) return;

  // The giant colourway name takes its colour from --lens. Always set a
  // fallback: getComputedStyle on a missing custom property returns an empty
  // string, and --lens then resolves to nothing at all.
  if (variant.accent_colour) {
    document.documentElement.style.setProperty('--lens', variant.accent_colour);
  }

  document.dispatchEvent(new CustomEvent(EVENT, { detail: variant }));
}

document.addEventListener(StandardEvents.productSelect, (event) => {
  // Product cards in a rail or grid each fire their own productSelect. Only the
  // main product section drives the page.
  if (event.target instanceof Element && event.target.closest('product-card')) return;

  const promise = /** @type {any} */ (event).promise;
  if (!promise?.then) {
    broadcast(readVariantData());
    return;
  }

  promise
    .then((/** @type {any} */ resolved) => {
      const html = resolved?.detail?.html;
      broadcast(readVariantData(html) || readVariantData());
    })
    .catch(() => {
      /* The picker aborts in-flight requests when the visitor clicks quickly.
         An aborted fetch is not an error worth surfacing. */
    });
});

/* Announce the server-rendered starting state once, so sections that render
   variant-dependent content do not have to duplicate the first-paint values. */
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => broadcast(readVariantData()), { once: true });
} else {
  broadcast(readVariantData());
}

export { EVENT as HystrideVariantEvent, readVariantData };
